/**
 * Feb 2026 — CEO Menu Organizer (Phase 1A.1)
 * ==========================================
 * /admin/creative/menu-organizer
 *
 * Extended in 1A.1:
 *   • Section-grouped view (Top/Header, Creator, Growth, Support,
 *     Creator Tools, Account, Admin utility buttons).
 *   • Mobile-friendly stacked card rows — full label, route, section,
 *     switches, move arrows, and Remove button no longer clipped on iPhone.
 *   • Search-filter approved-route picker with section selection.
 *   • Remove-from-Menu for non-locked entries (does NOT delete the
 *     underlying route / page / feature).
 *
 * Reuses the SAME menu_layout backend document + allow-list. No new
 * admin system. Never grants authorization to any route.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { invalidateMenuLayoutCache } from "@/lib/useMenuLayout";

// Order + labels for the visible section headers in the editor.
const SECTIONS = [
  { key: "top",             label: "Top / Header Navigation",         hint: "Desktop header + mobile drawer main list" },
  { key: "drawer_creator",  label: "Drawer · Creator",                hint: "Mobile hamburger Creator section (logged-in)" },
  { key: "drawer_growth",   label: "Drawer · Growth",                 hint: "Mobile hamburger Growth section (logged-in)" },
  { key: "drawer_support",  label: "Drawer · Support",                hint: "Mobile hamburger Support section" },
  { key: "drawer_tools",    label: "Drawer · Creator Tools",          hint: "Mobile hamburger Creator Tools (logged-in)" },
  { key: "account",         label: "Account",                         hint: "Mobile hamburger Account (logged-in)" },
];

const AdminMenuOrganizerPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(null);
  const [approvedPublic, setApprovedPublic] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSection, setPickerSection] = useState("top");
  const [pickerQuery, setPickerQuery] = useState("");

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const u = r.data;
      const isFounder = (u?.badges || []).includes("founder");
      const isAdmin = u?.role === "admin" || (u?.badges || []).includes("admin");
      if (!isFounder && !isAdmin) {
        toast.error("Admin access required");
        navigate("/home");
        return;
      }
      setMe(u);
    }).catch(() => navigate("/login"));
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/menu-layout");
      setLayout({ items: r.data.items, admin_buttons: r.data.admin_buttons });
      setApprovedPublic(r.data.approved_public_routes || []);
    } catch (e) {
      toast.error("Failed to load menu layout");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (me) load(); }, [me]);

  const grouped = useMemo(() => {
    const map = {};
    for (const s of SECTIONS) map[s.key] = [];
    for (const it of (layout?.items || [])) {
      const key = it.section && map[it.section] ? it.section : "top";
      map[key].push(it);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return map;
  }, [layout]);

  const currentIds = useMemo(
    () => new Set((layout?.items || []).map((i) => i.id)),
    [layout]
  );
  const addableRoutes = useMemo(
    () => approvedPublic
      .filter((r) => !currentIds.has(r.id))
      .filter((r) => !pickerQuery ||
        r.label.toLowerCase().includes(pickerQuery.toLowerCase()) ||
        r.route.toLowerCase().includes(pickerQuery.toLowerCase()) ||
        r.id.toLowerCase().includes(pickerQuery.toLowerCase())),
    [approvedPublic, currentIds, pickerQuery]
  );

  const patchItem = (id, patch) => {
    setLayout((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    }));
  };
  const moveWithinSection = (id, dir) => {
    setLayout((prev) => {
      const list = [...prev.items];
      const target = list.find((i) => i.id === id);
      if (!target) return prev;
      const sameSection = list.filter((i) => i.section === target.section);
      sameSection.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = sameSection.findIndex((i) => i.id === id);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= sameSection.length) return prev;
      const A = sameSection[idx], B = sameSection[swap];
      const AO = A.order, BO = B.order;
      return {
        ...prev,
        items: list.map((i) =>
          i.id === A.id ? { ...i, order: BO } : (i.id === B.id ? { ...i, order: AO } : i)
        ),
      };
    });
  };
  const removeItem = (id) => {
    setLayout((prev) => ({ ...prev, items: prev.items.filter((i) => i.id !== id) }));
  };
  const addExisting = (id) => {
    const meta = approvedPublic.find((r) => r.id === id);
    if (!meta) return;
    setLayout((prev) => {
      const sect = pickerSection && SECTIONS.some((s) => s.key === pickerSection)
        ? pickerSection
        : (meta.section || "top");
      const orderInSection = (prev.items || []).filter((i) => i.section === sect).length;
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            ...meta,
            section: sect,
            desktop: sect === "top",
            mobile: true,
            visible: true,
            order: orderInSection,
            locked: false,
          },
        ],
      };
    });
    setPickerOpen(false);
    setPickerQuery("");
  };
  const patchAdmin = (id, patch) => {
    setLayout((prev) => ({
      ...prev,
      admin_buttons: prev.admin_buttons.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    }));
  };
  const moveAdmin = (id, dir) => {
    setLayout((prev) => {
      const list = [...prev.admin_buttons];
      const idx = list.findIndex((i) => i.id === id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= list.length) return prev;
      [list[idx], list[target]] = [list[target], list[idx]];
      return { ...prev, admin_buttons: list.map((i, k) => ({ ...i, order: k })) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/menu-layout", layout);
      invalidateMenuLayoutCache();
      toast.success("Menu layout saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const restore = async () => {
    if (!window.confirm("Restore the shipped default navigation? Your current customization will be removed.")) return;
    setSaving(true);
    try {
      await api.delete("/admin/menu-layout");
      invalidateMenuLayoutCache();
      toast.success("Defaults restored");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setSaving(false); }
  };

  if (!me || loading || !layout) {
    return (
      <div className="min-h-screen bg-[#0a0a0e] flex items-center justify-center" data-testid="menu-organizer-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  // ── Mobile-friendly stacked card renderer ────────────────────────
  const ItemCard = ({ item }) => (
    <div
      className="rounded-lg bg-white/[0.03] border border-white/10 p-3 flex flex-col gap-2"
      data-testid={`mo-row-${item.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-1 pt-0.5">
          <button
            type="button"
            onClick={() => moveWithinSection(item.id, -1)}
            className="text-gray-400 hover:text-white px-2 py-0.5 text-xs rounded bg-black/30"
            data-testid={`mo-up-${item.id}`}
            aria-label="Move up"
          >▲</button>
          <button
            type="button"
            onClick={() => moveWithinSection(item.id, +1)}
            className="text-gray-400 hover:text-white px-2 py-0.5 text-xs rounded bg-black/30"
            data-testid={`mo-down-${item.id}`}
            aria-label="Move down"
          >▼</button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg">{item.icon}</span>
            <Input
              className="bg-black/40 border-white/10 text-white text-sm h-8 max-w-[260px] flex-1 min-w-[140px]"
              value={item.label}
              onChange={(e) => patchItem(item.id, { label: e.target.value })}
              data-testid={`mo-label-${item.id}`}
              disabled={item.locked}
            />
            {item.locked && (
              <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/20 text-amber-200 shrink-0" data-testid={`mo-locked-${item.id}`}>Locked</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[10px] text-gray-500">
            <span className="truncate max-w-[240px]">{item.route}</span>
            <span className="px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-200">{item.section}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-11">
        <label className="flex items-center gap-1.5 text-[11px] text-gray-300">
          Desktop
          <Switch
            checked={!!item.desktop}
            disabled={item.locked}
            onCheckedChange={(v) => patchItem(item.id, { desktop: v })}
            data-testid={`mo-desktop-${item.id}`}
          />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-gray-300">
          Mobile
          <Switch
            checked={!!item.mobile}
            disabled={item.locked}
            onCheckedChange={(v) => patchItem(item.id, { mobile: v })}
            data-testid={`mo-mobile-${item.id}`}
          />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-gray-300">
          Show
          <Switch
            checked={!!item.visible}
            disabled={item.locked}
            onCheckedChange={(v) => patchItem(item.id, { visible: v })}
            data-testid={`mo-visible-${item.id}`}
          />
        </label>
        {!item.locked && (
          <button
            type="button"
            onClick={() => removeItem(item.id)}
            className="ml-auto text-[11px] text-rose-300 hover:text-rose-200 underline"
            data-testid={`mo-remove-${item.id}`}
          >Remove from Menu</button>
        )}
      </div>
    </div>
  );

  const AdminCard = ({ item }) => (
    <div
      className="rounded-lg bg-white/[0.03] border border-white/10 p-3 flex flex-col gap-2"
      data-testid={`mo-admin-row-${item.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-1 pt-0.5">
          <button type="button" onClick={() => moveAdmin(item.id, -1)} className="text-gray-400 hover:text-white px-2 py-0.5 text-xs rounded bg-black/30" data-testid={`mo-admin-up-${item.id}`} aria-label="Move up">▲</button>
          <button type="button" onClick={() => moveAdmin(item.id, +1)} className="text-gray-400 hover:text-white px-2 py-0.5 text-xs rounded bg-black/30" data-testid={`mo-admin-down-${item.id}`} aria-label="Move down">▼</button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              className="bg-black/40 border-white/10 text-white text-sm h-8 max-w-[280px] flex-1 min-w-[140px]"
              value={item.label}
              onChange={(e) => patchAdmin(item.id, { label: e.target.value })}
              data-testid={`mo-admin-label-${item.id}`}
              disabled={item.locked}
            />
            {item.locked && <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/20 text-amber-200" data-testid={`mo-admin-locked-${item.id}`}>Locked</span>}
          </div>
          <div className="mt-1 text-[10px] text-gray-500 truncate">{item.route}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-11">
        <label className="flex items-center gap-1.5 text-[11px] text-gray-300">
          Show
          <Switch
            checked={!!item.visible}
            disabled={item.locked}
            onCheckedChange={(v) => patchAdmin(item.id, { visible: v })}
            data-testid={`mo-admin-visible-${item.id}`}
          />
        </label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0e] text-white" data-testid="menu-organizer-page">
      <SharedNavigation currentUser={me} activePage="admin" />
      <div className="pt-20 max-w-4xl mx-auto p-3 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-black">🧭 Menu Organizer</h1>
            <p className="text-[11px] sm:text-xs text-gray-400 mt-1">
              Shape the RGM navigation. Hide / Remove ≠ Delete — routes, pages, features and data stay intact.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin"><Button variant="outline" size="sm" className="border-gray-700 text-white" data-testid="mo-back-admin">← Admin</Button></Link>
          </div>
        </div>

        <div className="mb-4">
          <Button
            onClick={() => setPickerOpen((v) => !v)}
            variant="outline"
            className="border-purple-500/40 text-white w-full sm:w-auto"
            data-testid="mo-add-route-btn"
          >
            ＋ Add Existing Route ({addableRoutes.length})
          </Button>
          {pickerOpen && (
            <div className="mt-3 p-3 rounded-lg bg-black/40 border border-white/10 space-y-3" data-testid="mo-route-picker">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  className="bg-black/60 border-white/10 text-white text-sm"
                  placeholder="Search approved routes…"
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  data-testid="mo-picker-search"
                />
                <select
                  value={pickerSection}
                  onChange={(e) => setPickerSection(e.target.value)}
                  className="bg-black/60 border border-white/10 rounded px-2 py-1 text-sm text-white"
                  data-testid="mo-picker-section"
                >
                  {SECTIONS.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {addableRoutes.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => addExisting(r.id)}
                    className="text-left px-3 py-2 rounded bg-black/40 border border-white/10 hover:border-purple-400/40 text-sm"
                    data-testid={`mo-pick-${r.id}`}
                  >
                    <span className="mr-2">{r.icon}</span>{r.label}
                    <span className="ml-2 text-[10px] text-gray-500">{r.route}</span>
                  </button>
                ))}
                {addableRoutes.length === 0 && (
                  <p className="text-xs text-gray-500 sm:col-span-2" data-testid="mo-picker-empty">
                    {pickerQuery
                      ? "No approved routes match this search. Clear the search to browse everything, or Remove any row above and it will re-appear here."
                      : "Every approved route is already in the menu. Use “Remove from Menu” on any non-locked row above to move it back into this list."}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {SECTIONS.map((s) => (
          <Card key={s.key} className="p-3 sm:p-4 mb-4 bg-white/[0.03] border-white/10" data-testid={`mo-section-${s.key}`}>
            <div className="mb-2">
              <h2 className="text-base sm:text-lg font-bold">{s.label}</h2>
              <p className="text-[10px] text-gray-500">{s.hint}</p>
            </div>
            <div className="space-y-2">
              {grouped[s.key].length === 0 ? (
                <p className="text-[11px] text-gray-500 italic">No entries — use “Add Existing Route”.</p>
              ) : (
                grouped[s.key].map((i) => <ItemCard key={i.id} item={i} />)
              )}
            </div>
          </Card>
        ))}

        <Card className="p-3 sm:p-4 mb-6 bg-white/[0.03] border-white/10" data-testid="mo-section-admin-buttons">
          <div className="mb-2">
            <h2 className="text-base sm:text-lg font-bold">Admin Utility Buttons</h2>
            <p className="text-[10px] text-gray-500">Sensitive destinations (Payouts, Audit, Reserve writes, Stripe Diag) are never configurable here.</p>
          </div>
          <div className="space-y-2" data-testid="mo-admin-list">
            {layout.admin_buttons.map((i) => <AdminCard key={i.id} item={i} />)}
          </div>
        </Card>

        <div className="flex flex-wrap gap-3 sticky bottom-3">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black flex-1 sm:flex-none"
            data-testid="mo-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            onClick={restore}
            disabled={saving}
            variant="outline"
            className="border-amber-500/40 text-amber-100 flex-1 sm:flex-none"
            data-testid="mo-restore"
          >
            ↺ Restore Defaults
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminMenuOrganizerPage;
